import { Injectable } from '@angular/core';
import {
  Observable,
  concatMap,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  toArray,
} from 'rxjs';
import {
  CreateGroupSessionModel,
  GroupSessionMemberModel,
  GroupSessionModel,
  PatientSessionModel,
} from '../api/models';
import {
  OfflineDataService,
  PatientOfflineData,
} from './offline-data.service';
import { StaffApiService } from '../api/services';
import '../../shared/utils/utils';

@Injectable({ providedIn: 'root' })
export class GroupSessionService {
  private reconciliationInFlight: Observable<boolean> | null = null;

  constructor(
    private staffApiService: StaffApiService,
    private offlineDataService: OfflineDataService
  ) {}

  getGroupSession(groupSessionID: number): Observable<GroupSessionModel> {
    const cached =
      this.offlineDataService.getOfflineGroupSession(groupSessionID);
    if (
      cached &&
      (groupSessionID < 0 || this.offlineDataService.isInOfflineMode())
    ) {
      return of(cached);
    }

    if (this.offlineDataService.isInOfflineMode()) {
      return this.offlineDataService
        .getOfflineGroupSessionsObservable()
        .pipe(
          map((groups) => groups.get(groupSessionID) ?? null),
          filter((group): group is GroupSessionModel => !!group),
          take(1)
        );
    }

    return this.staffApiService
      .GetGroupSession({ groupSessionID })
      .pipe(
        switchMap((group) => this.cacheGroupAndPatients(group)),
        map(() => this.offlineDataService.getOfflineGroupSession(groupSessionID)!)
      );
  }

  createGroupSession(
    patientIDs: number[],
    isAcademySession: boolean
  ): Observable<GroupSessionModel> {
    const distinctPatientIDs = Array.from(new Set(patientIDs));
    if (distinctPatientIDs.length < 2) {
      throw new Error('Select at least two clients');
    }

    if (this.offlineDataService.isInOfflineMode()) {
      return from(
        this.createOfflineGroup(distinctPatientIDs, isAcademySession)
      );
    }

    const body: CreateGroupSessionModel = {
      ClientOperationID: this.createClientOperationID('online-group'),
      PatientIDs: distinctPatientIDs,
      IsAcademySession: isAcademySession,
    };
    return this.staffApiService
      .CreateGroupSession({ body })
      .pipe(switchMap((group) => this.cacheGroupAndPatients(group)));
  }

  reconcilePendingGroups(): Observable<boolean> {
    if (this.offlineDataService.isInOfflineMode()) {
      return of(false);
    }
    if (this.reconciliationInFlight) {
      return this.reconciliationInFlight;
    }

    const pending = this.offlineDataService
      .getOfflineGroupSessions()
      .filter((group) => group.ID < 0 && group.isDirty);
    if (pending.length === 0) {
      return of(true);
    }

    const reconciliation = forkJoin(
      pending.map((group) => {
        const clientOperationID =
          group.ClientOperationID ??
          this.createClientOperationID('offline-group');
        group.ClientOperationID = clientOperationID;
        return from(
          this.offlineDataService.saveOfflineGroupSession(group)
        ).pipe(
          switchMap(() =>
            this.staffApiService.CreateGroupSession({
              body: {
                ClientOperationID: clientOperationID,
                PatientIDs: group.Members.map((member) => member.PatientID),
                IsAcademySession: this.isAcademyGroup(group),
              } satisfies CreateGroupSessionModel,
            })
          ),
          switchMap((serverGroup) =>
            from(this.reconcileGroup(group, serverGroup))
          )
        );
      })
    ).pipe(
      map(() => true),
      finalize(() => {
        if (this.reconciliationInFlight === reconciliation) {
          this.reconciliationInFlight = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.reconciliationInFlight = reconciliation;
    return reconciliation;
  }

  private cacheGroupAndPatients(
    group: GroupSessionModel
  ): Observable<GroupSessionModel> {
    const patientLoads =
      group.Members.length > 0
        ? from(group.Members).pipe(
            concatMap((member) =>
              this.offlineDataService.savePatientForOffline(
                member.PatientID,
                false,
                member.SessionID > 0 ? member.SessionID : undefined
              )
            ),
            toArray()
          )
        : of([]);
    return patientLoads.pipe(
      switchMap(() =>
        from(
          this.offlineDataService.saveOfflineGroupSession({
            ...group,
            isDirty: false,
          })
        )
      ),
      map(() => group)
    );
  }

  private async createOfflineGroup(
    patientIDs: number[],
    isAcademySession: boolean
  ): Promise<GroupSessionModel> {
    const temporaryGroupID = -Date.now();
    const members: GroupSessionMemberModel[] = [];
    const patientDataByID = new Map<number, PatientOfflineData>();

    for (const patientID of patientIDs) {
      const patientData =
        this.offlineDataService.getOfflinePatientData(patientID);
      if (!patientData) {
        throw new Error(
          `Client ${patientID} is not available offline. Download the client before creating the group.`
        );
      }
      patientDataByID.set(patientID, patientData);
    }

    for (let index = 0; index < patientIDs.length; index++) {
      const patientID = patientIDs[index];
      const patientData = patientDataByID.get(patientID)!;
      const sessionID = temporaryGroupID - index - 1;
      const session: PatientSessionModel & { isDirty?: boolean } = {
        ID: sessionID,
        PatientID: patientID,
        GroupSessionID: temporaryGroupID,
        IsAcademySession: isAcademySession,
        TargetData: [],
        BehaviorData: [],
      };
      await this.offlineDataService.saveOfflineSessionData(
        patientID,
        session,
        true,
        false
      );
      members.push({
        PatientID: patientID,
        PatientName: this.getPatientName(patientData.patient),
        SessionID: sessionID,
      });
    }

    const group: GroupSessionModel = {
      ClientOperationID: this.createClientOperationID('offline-group'),
      ID: temporaryGroupID,
      CreatedDate: new Date().toServerString(),
      Members: members,
      isDirty: true,
    };
    await this.offlineDataService.saveOfflineGroupSession(group);
    return group;
  }

  private async reconcileGroup(
    localGroup: GroupSessionModel,
    serverGroup: GroupSessionModel
  ): Promise<void> {
    const memberMappings = localGroup.Members.map((localMember) => {
      const serverMember = serverGroup.Members.find(
        (member) => member.PatientID === localMember.PatientID
      );
      const patientData = this.offlineDataService.getOfflinePatientData(
        localMember.PatientID
      );
      const localSession = patientData?.sessions.find(
        (session) => session.ID === localMember.SessionID
      );
      if (!serverMember || !localSession) {
        throw new Error(
          `Unable to reconcile group member for client ${localMember.PatientID}`
        );
      }
      return { localMember, serverMember, localSession };
    });

    for (const { localMember, serverMember, localSession } of memberMappings) {
      const reconciledSession: PatientSessionModel = {
        ...localSession,
        ID: serverMember.SessionID,
        GroupSessionID: serverGroup.ID,
        TargetData: localSession.TargetData?.map((data) => ({
          ...data,
          PatientSessionID: serverMember.SessionID,
        })),
        BehaviorData: localSession.BehaviorData?.map((data) => ({
          ...data,
          PatientSessionID: serverMember.SessionID,
        })),
      };
      await this.offlineDataService.rekeyOfflineSessionArtifacts(
        localMember.PatientID,
        localMember.SessionID,
        serverMember.SessionID
      );
      this.rekeyRunningTimers(
        localMember.SessionID,
        serverMember.SessionID
      );
      await this.offlineDataService.removeOfflineSessionData(
        localMember.PatientID,
        localMember.SessionID
      );
      await this.offlineDataService.saveOfflineSessionData(
        localMember.PatientID,
        reconciledSession,
        true,
        false
      );
      localMember.SessionID = serverMember.SessionID;
      localMember.StartedDate = reconciledSession.StartedDate;
      localMember.FinishedDate = reconciledSession.FinishedDate;
    }

    const temporaryGroupID = localGroup.ID;
    localGroup.ID = serverGroup.ID;
    localGroup.ServiceID = serverGroup.ServiceID;
    localGroup.CreatedDate = serverGroup.CreatedDate;
    localGroup.CreatedByUserID = serverGroup.CreatedByUserID;
    localGroup.isDirty = false;
    await this.offlineDataService.replaceOfflineGroupSession(
      temporaryGroupID,
      localGroup
    );

    if (window.location.pathname.includes(`/staff/group/${temporaryGroupID}`)) {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname.replace(
          `/staff/group/${temporaryGroupID}`,
          `/staff/group/${serverGroup.ID}`
        )}${window.location.search}`
      );
    }
  }

  private getPatientName(patient: {
    FirstName?: string | null;
    LastName?: string | null;
    PreferredName?: string | null;
  }): string {
    return (
      patient.PreferredName ||
      [patient.FirstName, patient.LastName].filter(Boolean).join(' ') ||
      'Client'
    );
  }

  private createClientOperationID(prefix: string): string {
    const randomID =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}:${randomID}`;
  }

  private isAcademyGroup(group: GroupSessionModel): boolean {
    return group.Members.some((member) => {
      const patientData = this.offlineDataService.getOfflinePatientData(
        member.PatientID
      );
      return patientData?.sessions.some(
        (session) =>
          session.ID === member.SessionID && session.IsAcademySession
      );
    });
  }

  private rekeyRunningTimers(
    oldSessionID: number,
    newSessionID: number
  ): void {
    const storedData = localStorage.getItem('targetsRunningData');
    if (storedData) {
      const runningData = JSON.parse(storedData) as {
        SessionID: number;
        TargetID: number;
        Data: unknown;
      }[];
      runningData.forEach((item) => {
        if (item.SessionID === oldSessionID) {
          item.SessionID = newSessionID;
        }
      });
      localStorage.setItem('targetsRunningData', JSON.stringify(runningData));
    }
    window.dispatchEvent(
      new CustomEvent('sparkSessionIDReconciled', {
        detail: { oldSessionID, newSessionID },
      })
    );
  }
}
